import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  EmptyState,
  Flex,
  Form,
  Heading,
  Icon,
  Icons,
  Modal,
  Paragraph,
  Spin,
  Table,
  useToast,
  type TableColumnType,
} from "@ir/ui";
import {
  CreatePurchaserPortalKeyDTO,
  UpdatePurchaserPortalKeyDTO,
} from "@src/domain/dto/purchasers";
import type { PurchaserPortalKey } from "@src/domain/entities/purchaser";
import { usePurchasersData } from "@src/presentation/providers/PurchasersDataProvider";
import { PurchaserPortalKeyForm } from "./PurchaserPortalKeyForm";
import {
  getPurchaserPortalKeyFieldError,
  getPurchaserPortalKeyLoadErrorMessage,
  getPurchaserPortalKeySaveErrorMessage,
} from "./purchaserPortalKeyErrors";
import {
  EMPTY_PORTAL_KEY_FORM_VALUES,
  formatPortalKey,
  type PurchaserPortalKeyFormValues,
} from "./purchaserPortalKeyFormUtils";

interface PurchaserPortalKeyModalProps {
  purchaserId: number | null;
  purchaserKey: string;
  purchaserName?: string;
  onClose: () => void;
}

interface PortalKeyRow {
  key: string;
  portalKeyName: string;
  portalKey: string;
  record: PurchaserPortalKey;
}

type FormMode = "create" | "edit" | null;

function sortPortalKeys(keys: PurchaserPortalKey[]): PurchaserPortalKey[] {
  return [...keys].sort((left, right) => {
    const byName = left.portalKeyName.localeCompare(right.portalKeyName, undefined, {
      sensitivity: "base",
    });
    if (byName !== 0) return byName;
    return left.id - right.id;
  });
}

export const PurchaserPortalKeyModal = ({
  purchaserId,
  purchaserKey,
  purchaserName,
  onClose,
}: PurchaserPortalKeyModalProps) => {
  const { success: successToast, error: errorToast } = useToast();
  const onCloseRef = useRef(onClose);
  const errorToastRef = useRef(errorToast);
  onCloseRef.current = onClose;
  errorToastRef.current = errorToast;
  const {
    getPurchaserPortalKeys,
    getCachedPurchaserPortalKeys,
    createPurchaserPortalKey,
    updatePurchaserPortalKey,
  } = usePurchasersData();
  const [form] = Form.useForm<PurchaserPortalKeyFormValues>();
  const [portalKeys, setPortalKeys] = useState<PurchaserPortalKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [editingKey, setEditingKey] = useState<PurchaserPortalKey | null>(null);
  const [initialValues, setInitialValues] = useState<PurchaserPortalKeyFormValues>(
    EMPTY_PORTAL_KEY_FORM_VALUES,
  );
  const [isFormSubmittable, setIsFormSubmittable] = useState(false);

  const isOpen = purchaserId != null;

  const closeForm = useCallback(() => {
    setFormMode(null);
    setEditingKey(null);
    setInitialValues(EMPTY_PORTAL_KEY_FORM_VALUES);
    setIsFormSubmittable(false);
    form.resetFields();
  }, [form]);

  useEffect(() => {
    if (purchaserId == null) {
      setPortalKeys([]);
      setFormMode(null);
      setEditingKey(null);
      setInitialValues(EMPTY_PORTAL_KEY_FORM_VALUES);
      setIsFormSubmittable(false);
      return;
    }

    setFormMode(null);
    setEditingKey(null);
    setInitialValues(EMPTY_PORTAL_KEY_FORM_VALUES);
    setIsFormSubmittable(false);

    const cachedKeys = getCachedPurchaserPortalKeys(purchaserId);
    if (cachedKeys !== undefined) {
      setPortalKeys(sortPortalKeys(cachedKeys));
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    void (async () => {
      try {
        const keys = await getPurchaserPortalKeys(purchaserId);
        if (cancelled) return;
        setPortalKeys(sortPortalKeys(keys));
      } catch (err) {
        if (!cancelled) {
          errorToastRef.current({
            content: getPurchaserPortalKeyLoadErrorMessage(err),
          });
          onCloseRef.current();
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getCachedPurchaserPortalKeys, getPurchaserPortalKeys, purchaserId]);

  const openCreateForm = useCallback(() => {
    setEditingKey(null);
    setInitialValues(EMPTY_PORTAL_KEY_FORM_VALUES);
    setFormMode("create");
    setIsFormSubmittable(false);
    form.setFieldsValue(EMPTY_PORTAL_KEY_FORM_VALUES);
  }, [form]);

  const openEditForm = useCallback(
    (record: PurchaserPortalKey) => {
      const nextValues: PurchaserPortalKeyFormValues = {
        portalKeyName: record.portalKeyName,
        portalKey: record.portalKey,
      };
      setEditingKey(record);
      setInitialValues(nextValues);
      setFormMode("edit");
      setIsFormSubmittable(false);
      form.setFieldsValue(nextValues);
    },
    [form],
  );

  const handleSubmit = useCallback(
    async (values: PurchaserPortalKeyFormValues) => {
      if (purchaserId == null || formMode == null) return;

      setIsSaving(true);
      try {
        if (formMode === "create") {
          const created = await createPurchaserPortalKey(
            new CreatePurchaserPortalKeyDTO({
              purchaserId,
              purchaserKey,
              portalKeyName: values.portalKeyName.trim(),
              portalKey: formatPortalKey(values.portalKey),
            }),
          );
          setPortalKeys((current) => sortPortalKeys([...current, created]));
          successToast({ content: "Portal key created successfully" });
        } else if (editingKey) {
          const updated = await updatePurchaserPortalKey(
            new UpdatePurchaserPortalKeyDTO({
              id: editingKey.id,
              purchaserId,
              purchaserKey,
              portalKeyName: values.portalKeyName.trim(),
            }),
          );
          setPortalKeys((current) =>
            sortPortalKeys(
              current.map((item) => (item.id === updated.id ? updated : item)),
            ),
          );
          successToast({ content: "Portal key updated successfully" });
        }
        closeForm();
      } catch (err) {
        const fieldError = getPurchaserPortalKeyFieldError(err);
        if (fieldError) {
          form.setFields([
            {
              name: "portalKey",
              errors: [fieldError],
            },
          ]);
          setIsFormSubmittable(false);
        } else {
          errorToast({
            content: getPurchaserPortalKeySaveErrorMessage(err),
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeForm,
      createPurchaserPortalKey,
      editingKey,
      errorToast,
      form,
      formMode,
      purchaserId,
      purchaserKey,
      successToast,
      updatePurchaserPortalKey,
    ],
  );

  const columns = useMemo<TableColumnType<PortalKeyRow>[]>(
    () => [
      {
        title: "Portal Key Name",
        dataIndex: "portalKeyName",
        key: "portalKeyName",
        cellRender: (value: unknown) => (
          <Paragraph className="m-0">{value as string}</Paragraph>
        ),
      },
      {
        title: "Portal Key",
        dataIndex: "portalKey",
        key: "portalKey",
        cellRender: (value: unknown) => (
          <Paragraph className="m-0 text-grey-500">{value as string}</Paragraph>
        ),
      },
      {
        title: "Edit",
        dataIndex: "record",
        key: "edit",
        width: 80,
        align: "center",
        cellRender: (_value: unknown, record: PortalKeyRow) => (
          <Button
            size="small"
            color="grey"
            variant="clear"
            isDisabled={isSaving || formMode != null}
            onPress={() => openEditForm(record.record)}
          >
            <Icon name={Icons.Edit} />
          </Button>
        ),
      },
    ],
    [formMode, isSaving, openEditForm],
  );

  const rows = useMemo<PortalKeyRow[]>(
    () =>
      portalKeys.map((record) => ({
        key: String(record.id),
        portalKeyName: record.portalKeyName,
        portalKey: record.portalKey,
        record,
      })),
    [portalKeys],
  );

  let body = null;
  if (isLoading && portalKeys.length === 0) {
    body = (
      <Flex align="center" justifyContent="center" className="py-6">
        <Spin tip="Loading..." spinning />
      </Flex>
    );
  } else if (portalKeys.length === 0) {
    body = (
      <>
        {formMode ? (
          <PurchaserPortalKeyForm
            form={form}
            mode={formMode}
            initialValues={initialValues}
            isSaving={isSaving}
            isFormSubmittable={isFormSubmittable}
            onCancel={closeForm}
            onSubmit={handleSubmit}
            onFormValidityChange={setIsFormSubmittable}
          />
        ) : null}
        <EmptyState
          className="bg-white border-1 border-color-grey-25 px-4 py-5 m-4"
          icon={Icons.InfoOctagon}
          title={
            <Heading level={6} className="text-grey-700 font-bold mt-3">
              No portal keys for this purchaser.
            </Heading>
          }
        />
      </>
    );
  } else {
    body = (
      <>
        {formMode ? (
          <PurchaserPortalKeyForm
            form={form}
            mode={formMode}
            initialValues={initialValues}
            isSaving={isSaving}
            isFormSubmittable={isFormSubmittable}
            onCancel={closeForm}
            onSubmit={handleSubmit}
            onFormValidityChange={setIsFormSubmittable}
          />
        ) : null}
        <Table<PortalKeyRow>
          columns={columns}
          dataSource={rows}
          rowHoverable
          aria-label="Portal keys"
        />
      </>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      closable
      headerContent={
        <Flex
          align="center"
          justifyContent="space-between"
          gap={1}
          className="w-100"
        >
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Portal Key
            {purchaserName ? ` — ${purchaserName}` : ""}
          </Heading>
          <Button
            size="small"
            variant="outline"
            onPress={openCreateForm}
            isDisabled={isLoading || isSaving || formMode != null}
          >
            <Icon slot="start" name={Icons.Plus} />
            Add
          </Button>
        </Flex>
      }
      footerContent={
        <Button size="small" color="grey" onPress={onClose} isDisabled={isLoading || isSaving}>
          Close
        </Button>
      }
    >
      {isOpen ? body : null}
    </Modal>
  );
};
